import { useContext } from 'react';
import * as R from 'ramda';

import { usePreferences } from '@atom/hooks/usePreferences';
import { Progress } from '@atom/mui';
import { AssetUsageSummary } from '@atom/types/asset';
import { ConversionUnit } from '@atom/types/schema';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import InventoryDetailContext from '../InventoryDetailContext';

import CostHeaderBlock from './CostHeaderBlock';
import {
  formatQuantityWithUnit,
  formatRateWithUnit,
  toConvertedQuantity,
  toConvertedRate,
} from './costTabUtilities';

import classes from './costTab.module.css';

export type CostHeaderProps = {
  summary?: AssetUsageSummary;
  summaryLoading: boolean;
  conversionUnit?: ConversionUnit;
};

const CostHeader = ({
  summary,
  summaryLoading,
  conversionUnit,
}: CostHeaderProps) => {
  const { asset } = useContext(InventoryDetailContext);

  const preferences = usePreferences();
  const fiscalStartDate = R.pathOr(null, ['fiscalStartDate'], preferences);

  const unit = asset?.budget?.unit;
  const showStockBasedSections = asset?.budget?.isStockBased;

  const getQuantitySubtext = (input?: number): string | undefined =>
    conversionUnit &&
    formatQuantityWithUnit(
      toConvertedQuantity(input, conversionUnit),
      conversionUnit.unit,
    );

  const rateSubtext =
    conversionUnit &&
    formatRateWithUnit(
      toConvertedRate(summary?.rate, conversionUnit),
      conversionUnit.unit,
    );

  return (
    <div className={classes.rightPaneHeader} data-cy="costHeader">
      {summaryLoading ? (
        <Progress size={20} />
      ) : (
        <>
          {showStockBasedSections && (
            <>
              <CostHeaderBlock
                title="Total Remaining"
                value={formatQuantityWithUnit(summary?.totalRemaining, unit)}
                subtext={getQuantitySubtext(summary?.totalRemaining)}
                dataCy="costHeaderTotalRemaining"
              />
              <CostHeaderBlock
                title="Today's Usage"
                value={formatQuantityWithUnit(summary?.totalUsageToday, unit)}
                subtext={getQuantitySubtext(summary?.totalUsageToday)}
                dataCy="costHeaderTodaysUsage"
              />
            </>
          )}
          <CostHeaderBlock
            title="Total Usage"
            value={formatQuantityWithUnit(summary?.totalUsage, unit)}
            subtext={getQuantitySubtext(summary?.totalUsage)}
            dataCy="costHeaderTotalUsage"
          />
          <CostHeaderBlock
            title="Cost per Unit"
            value={formatRateWithUnit(summary?.rate, unit)}
            subtext={rateSubtext}
            dataCy="costHeaderCostPerUnit"
          />
          <CostHeaderBlock
            title={
              !isNilOrEmpty(fiscalStartDate)
                ? 'Total Cost (Fiscal Year)'
                : 'Total Cost (Year)'
            }
            value={numberToLocaleString(summary?.totalCostYear)}
            dataCy="costHeaderTotalCostYear"
          />
          <CostHeaderBlock
            title="Total Cost (Month)"
            value={numberToLocaleString(summary?.totalCostMonth)}
            dataCy="costHeaderTotalCostMonth"
          />
          <CostHeaderBlock
            title="Total Cost"
            value={numberToLocaleString(summary?.totalCost)}
            dataCy="costHeaderTotalCost"
          />
        </>
      )}
    </div>
  );
};

export default CostHeader;
